import { useState } from "react";
import { Pause, Pencil, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card, SectionTitle, Field, Badge } from "@/components/common";
import { run } from "@/modules/interventions/JobSections";
import { hm } from "@/modules/interventions/MyDay";
import { useGarage } from "@/context/GarageContext";
import { useFetch } from "@/lib/useFetch";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { t } from "@/i18n";

const toLocal = (iso) => { const d = iso ? new Date(iso) : new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };

function EditDialog({ entry, onClose, onSaved }) {
  const [f, setF] = useState({ started_at: toLocal(entry.started_at), ended_at: toLocal(entry.ended_at), reason: "" });
  const save = () => run(() => api.put(`/time-entries/${entry.id}`, { started_at: new Date(f.started_at).toISOString(), ended_at: new Date(f.ended_at).toISOString(), reason: f.reason }),
    (d) => { onSaved(d); onClose(); });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="time-edit-dialog">
        <DialogHeader><DialogTitle>{t("b12.time.editTitle")}</DialogTitle><DialogDescription>{t("b12.time.editHint", { who: entry.user })}</DialogDescription></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t("b12.time.start")}><Input type="datetime-local" value={f.started_at} onChange={(e) => setF({ ...f, started_at: e.target.value })} data-testid="time-edit-start" /></Field>
          <Field label={t("b12.time.end")}><Input type="datetime-local" value={f.ended_at} onChange={(e) => setF({ ...f, ended_at: e.target.value })} data-testid="time-edit-end" /></Field>
          <Field label={t("b12.time.reason")} className="sm:col-span-2"><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="time-edit-reason" /></Field>
        </div>
        <Button disabled={f.reason.trim().length < 3} onClick={save} data-testid="time-edit-save">{t("common.save")}</Button>
      </DialogContent>
    </Dialog>
  );
}

export function TimeCard({ job, readOnly }) {
  const { isManager, garage } = useGarage();
  const { data, setData, reload } = useFetch(`/jobs/${job.id}/time`);
  const [edit, setEdit] = useState(null);
  if (!data) return null;
  const set = setData || (() => reload());
  const over = data.quoted_minutes > 0 && data.logged_minutes > data.quoted_minutes;
  const pct = data.quoted_minutes ? Math.min(100, Math.round((100 * data.logged_minutes) / data.quoted_minutes)) : 0;
  const closed = ["completed", "cancelled"].includes(job.status);
  return (
    <Card testId="job-time-card">
      <SectionTitle hint={t("b12.time.hint")}>{t("b12.time.title")}</SectionTitle>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-display text-3xl font-extrabold" data-testid="time-logged">{hm(data.logged_minutes)}</p>
          <p className="text-sm text-slate-500" data-testid="time-quoted">{data.quoted_minutes ? t("b12.time.vsQuoted", { time: hm(data.quoted_minutes) }) : t("b12.time.noQuoted")}</p>
        </div>
        {!readOnly && !closed && (
          <Button variant={data.running ? "default" : "outline"} data-testid="job-timer-toggle"
            onClick={() => run(() => api.post(`/jobs/${job.id}/timer/${data.running ? "stop" : "start"}`), set, data.running ? t("b12.time.stopped") : t("b12.time.started"))}>
            {data.running ? <Pause className="mr-2 h-4 w-4" /> : <Play className="mr-2 h-4 w-4" />}{data.running ? t("b12.day.pause") : t("b12.day.start")}
          </Button>
        )}
      </div>
      {data.quoted_minutes > 0 && <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100"><div className={`h-full ${over ? "bg-red-500" : "bg-teal-500"}`} style={{ width: `${over ? 100 : pct}%` }} /></div>}
      <ul className="mt-4 max-h-64 space-y-1.5 overflow-y-auto text-sm">
        {data.entries.map((e) => (
          <li key={e.id} className="flex items-center gap-2 rounded-lg border border-slate-100 px-3 py-2" data-testid={`time-entry-${e.id}`}>
            <span className="min-w-0 flex-1"><strong>{e.user}</strong> <span className="text-xs text-slate-500">{fmtDateTime(e.started_at, garage.timezone)} → {e.ended_at ? fmtDateTime(e.ended_at, garage.timezone) : t("b12.time.running")}</span>
              {e.edits.length > 0 && <span className="block text-[11px] text-amber-700">{t("b12.time.edited", { reason: e.edits[e.edits.length - 1].reason, by: e.edits[e.edits.length - 1].by })}</span>}</span>
            {e.running ? <Badge tone="teal">{t("b12.time.running")}</Badge> : <span className="font-mono text-xs">{hm(e.minutes)}</span>}
            {isManager && !readOnly && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEdit(e)} data-testid={`time-entry-edit-${e.id}`} aria-label={t("common.edit")}><Pencil className="h-3.5 w-3.5" /></Button>}
          </li>
        ))}
        {data.entries.length === 0 && <li className="text-slate-500">{t("b12.time.none")}</li>}
      </ul>
      {edit && <EditDialog entry={edit} onClose={() => setEdit(null)} onSaved={set} />}
    </Card>
  );
}
